import React from 'react';
import { useTable, createDummyRow } from '../TableContext';
import { cn } from '../../../../utils/cn';
import { Skeleton } from '../../../atoms/Skeleton/Skeleton';
import { TableCell } from './TableCell';
import { TableRowSpacer } from './TableRowSpacer';
import { TableSubComponent } from './TableSubComponent';

export interface TableLoadingStateProps {
  rows?: number;
  className?: string;
  render?: (props: { columns: number }) => React.ReactNode;
  cellContent?: React.ReactNode[];
}

export function TableLoadingState({
  rows = 3,
  render,
  cellContent,
}: TableLoadingStateProps) {
  const { table, row, expansion } = useTable();
  const columns = table.getAllColumns().length;

  if (render) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns}>{render({ columns })}</td>
        </tr>
      </tbody>
    );
  }

  const dummyRow = createDummyRow('loading', columns);

  const getDefaultContent = (columnIndex: number) => (
    <div className="space-y-2">
      <Skeleton className={cn('h-5', columnIndex === 0 ? 'w-48' : 'w-32')} />
      {columnIndex === 0 && <Skeleton className="h-4 w-32" />}
    </div>
  );

  return (
    <tbody className="divide-y divide-gray-200/60 dark:divide-gray-700/60">
      {Array.from({ length: rows }).map((_, index) => (
        <React.Fragment key={index}>
          {row?.spacer && <TableRowSpacer />}
          <tr className="group">
            {dummyRow.getVisibleCells().map((cell, index) => {
              const columnIndex = cell.column.getIndex();

              return (
                <TableCell key={index} cell={cell} className="py-4">
                  {cellContent?.[columnIndex] || getDefaultContent(columnIndex)}
                </TableCell>
              );
            })}
          </tr>

          {expansion?.enabled && (
            <TableSubComponent
              row={dummyRow}
              toggle={{
                position: expansion.toggle?.position,
                // The toggle's shape without a button: there is nothing to
                // open yet, so nothing to focus.
                render: () => (
                  <div
                    aria-hidden="true"
                    className="bg-lightest-gray flex w-full items-center justify-center px-4 py-4"
                  >
                    <Skeleton className="h-4 w-56" />
                  </div>
                ),
              }}
            >
              <Skeleton className="h-4 w-56" />
            </TableSubComponent>
          )}
        </React.Fragment>
      ))}
    </tbody>
  );
}
